// =============================================================================
// useFangateCreatorNetwork
//
// "Creators you can tag again" — the co-creators who have ACCEPTED one of your
// tagging requests before, so you can re-tag them without a fresh approval.
// Used as a quick-select above the search field in the content Upload modal and
// the Create Bundle popup (and conceptually mirrors FangateCollaborations'
// section 4).
//
// Fangate exposes NO global "people I tagged who accepted" endpoint (verified
// against the API + docs): the only tagger-side source is per-product
// GET /products/{id}/consent-creator-tags. So we aggregate across the vault's
// products — expensive — and cache the result per inbox at module scope so the
// modals open instantly and the fetch runs once in the background (refreshed
// after a TTL).
// =============================================================================
import { reactive } from 'vue';
import FangateAPI from 'dashboard/api/fangate';

const TTL_MS = 10 * 60 * 1000;            // re-aggregate at most every 10 min
const cache = reactive({});               // inboxId -> { creators, ts, loading, error }

const nowMs = () => new Date().getTime();

const entryFor = id => {
  if (!cache[id]) cache[id] = { creators: [], ts: 0, loading: false, error: '' };
  return cache[id];
};

// Aggregate unique accepted co-creators across the vault's products.
async function buildNetwork() {
  const resp = await FangateAPI.listProducts({ page: 1, limit: 100 });
  const products = Array.isArray(resp) ? resp : (resp?.data ?? resp?.products ?? []);
  if (!products.length) return [];

  const batches = await Promise.all(
    products.map(p => FangateAPI.listProductConsentTags(p.id).catch(() => []))
  );
  const accepted = batches.flat().filter(t => t?.status === 'accepted');

  const byCreator = new Map();
  for (const tag of accepted) {
    const u = tag.tagged_user;
    if (!u?.id) continue;
    if (!byCreator.has(u.id)) {
      byCreator.set(u.id, { ...u, productCount: 0, lastAcceptedAt: tag.taken_at || tag.created_at });
    }
    const e = byCreator.get(u.id);
    e.productCount += 1;
    const stamp = tag.taken_at || tag.created_at;
    if (stamp && stamp > e.lastAcceptedAt) e.lastAcceptedAt = stamp;
  }
  return Array.from(byCreator.values())
    .sort((a, b) => String(b.lastAcceptedAt || '').localeCompare(String(a.lastAcceptedAt || '')));
}

export function useFangateCreatorNetwork() {
  // Reactive view of the cached entry for the currently-active vault inbox.
  const networkState = () => entryFor(FangateAPI.getActiveInbox() || '_');

  // Lazy, cached, background-safe. Call when the tagging UI opens; render chips
  // from networkState().creators once they land.
  const loadNetwork = async ({ force = false } = {}) => {
    const id = FangateAPI.getActiveInbox() || '_';
    const e = entryFor(id);
    if (e.loading) return;
    if (!force && e.ts && (nowMs() - e.ts) < TTL_MS) return;   // still fresh
    e.loading = true;
    e.error = '';
    try {
      e.creators = await buildNetwork();
      e.ts = nowMs();
    } catch (err) {
      e.error = err?.message || 'Failed to load collaborators.';
    } finally {
      e.loading = false;
    }
  };

  return { networkState, loadNetwork };
}
